// Code generated by tygo. DO NOT EDIT.
/* eslint-disable */
/* prettier-ignore */
import * as core from "../../../core/wire/core.gen"

//////////
// source: conversation.go

/**
 * ConversationView is one conversation as every member of it sees it. There is no per-viewer field
 * here: what only its owner may see lives in MemberSelf.
 * OwnerGroupIds are the live groups that own a channel, ascending: everybody in one of them finds
 * the channel, reads it and may join it. It is [] for every other kind, and for a channel whose
 * owner groups were all deleted, which only its members reach. A deleted group's row is not in it,
 * so the client never meets an id it cannot name.
 * AgentsAvailable is the conversation's switch for AI chats: while it is on, Claude in an AI chat
 * whose owners could all open the conversation may read it. AgentsSetBy and AgentsSetAt say who
 * last switched it and when; both are absent until somebody first does.
 */
export interface ConversationView {
	id: number /* int64 */;
	kind: string; // channel | private | dm | groupDm
	slug?: string;
	name?: string;
	topic?: string;
	purpose?: string;
	dmHandle?: string; // "anna+bert", the /d/ route segment of a dm or groupDm
	createdAt: number /* int64 */;
	updatedAt: number /* int64 */;
	archivedAt?: number /* int64 */;
	lastSeq: number /* int64 */;
	lastMessageAt?: number /* int64 */;
	memberCount: number /* int */;
	memberIds?: number /* int64 */[]; // inline while <= 50, else omitted and read from the member route
	defaultVisibleFromSeq: number /* int64 */;
	ownerGroupIds: number /* int64 */[];
	agentsAvailable: boolean;
	agentsSetBy?: number /* int64 */;
	agentsSetAt?: number /* int64 */;
}
/**
 * MemberPublic is the part of a membership row that may be broadcast: who is in, in which state,
 * and since when. It carries no read state and no preference (security.md §9.5).
 */
export interface MemberPublic {
	conv: number /* int64 */;
	userId: number /* int64 */;
	state: string; // active | left | removed
	role: string; // member | owner
	joinedAt: number /* int64 */;
	joinedSeq: number /* int64 */;
}
/**
 * MemberSelf is the whole membership row of one person, and it never leaves that person's
 * connections. MemberPublic's fields are written out rather than embedded, because the JSON shape is
 * flat and the TypeScript generator renders an embedded struct as a nested property.
 */
export interface MemberSelf {
	conv: number /* int64 */;
	userId: number /* int64 */;
	state: string;
	role: string;
	joinedAt: number /* int64 */;
	joinedSeq: number /* int64 */;
	visibleFromSeq: number /* int64 */;
	lastReadSeq: number /* int64 */;
	lastReadAt: number /* int64 */;
	readVersion: number /* int64 */;
	manualUnread: boolean;
	notifyLevel: string; // all | mention | none
	mutedUntil?: number /* int64 */;
	starred: boolean;
	hidden: boolean;
	sortHint?: number /* int */;
}
/**
 * MemberChanged carries the rows an add, a removal, a join or a leave touched, together with the
 * conversation's new active-member count. The paired system message travels as the seq-allocating
 * chat.message.new of the same commit.
 */
export interface MemberChanged {
	conv: number /* int64 */;
	members: MemberPublic[];
	memberCount: number /* int */;
}
/**
 * MembershipSelf is addressed to one person: their own row after an add, a leave, a removal or a
 * change of their private preferences. Conversation is set when the client may not know the
 * conversation yet — on an add and on a join — so the sidebar can insert it without a second call.
 */
export interface MembershipSelf {
	conv: number /* int64 */;
	self: MemberSelf;
	conversation?: ConversationView;
}
/**
 * BootstrapConversation is one row of the sidebar as rt.bootstrap delivers it: the conversation, the
 * reader's own membership and the two counters the badge is drawn from.
 */
export interface BootstrapConversation {
	conversation: ConversationView;
	self: MemberSelf;
	unread: number /* int */;
	mentions: number /* int */;
}
/**
 * ChatBootstrap is what modules["chat"] of the bootstrap payload holds: the sidebar's rows and one
 * digest per draft — target, rev, length, never a body (protocol.md §2.7).
 */
export interface ChatBootstrap {
	conversations: BootstrapConversation[];
	drafts: DraftDigest[];
}
/**
 * ConversationList is the answer of GET /api/chat/conversations — the same rows the bootstrap
 * carries, so the sidebar has one shape and one reducer.
 */
export interface ConversationList {
	conversations: BootstrapConversation[];
}
/**
 * CreateConversation is the body of POST /api/chat/conversations. Only a channel or a private
 * conversation is created here; a DM is get-or-create through POST /api/chat/dm.
 */
export interface CreateConversation {
	kind: string; // channel | private
	slug: string;
	name: string;
	topic?: string;
	purpose?: string;
	memberIds?: number /* int64 */[];
	/**
	 * OwnerGroupIds are the groups a channel belongs to: everybody in one of them finds it, reads it
	 * and may join it. Absent or empty is the team members group; a private channel takes none.
	 */
	ownerGroupIds?: number /* int64 */[];
}
/**
 * ConversationCreated is the answer of a create: the conversation, the creator's own row and the
 * members it starts with.
 */
export interface ConversationCreated {
	conversation: ConversationView;
	self: MemberSelf;
	members: MemberPublic[];
}
/**
 * ConversationRead is the answer of GET /api/chat/conversations/{id}. Self is null for a reader
 * who reads a channel their groups reach without having joined it.
 * Reach is how the reader holds the conversation: their own row, a group that owns the channel, or
 * an administrator's view of a channel or private channel they are not in — its head and its
 * members, never its messages.
 */
export interface ConversationRead {
	conversation: ConversationView;
	members: MemberPublic[];
	self?: MemberSelf;
	reach: 'member' | 'group' | 'admin';
}
/**
 * The three values of ConversationRead.Reach.
 */
export const ReachMember = "member";
/**
 * The three values of ConversationRead.Reach.
 */
export const ReachGroup = "group";
/**
 * The three values of ConversationRead.Reach.
 */
export const ReachAdmin = "admin";
/**
 * UpdateConversation is the body of PATCH /api/chat/conversations/{id}; an absent field is left as
 * it is, which is why every one is a pointer.
 */
export interface UpdateConversation {
	name?: string;
	slug?: string;
	topic?: string;
	purpose?: string;
}
/**
 * ConversationResponse is the answer of every route that returns the conversation alone.
 */
export interface ConversationResponse {
	conversation: ConversationView;
}
/**
 * ArchiveConversation is the body of POST /api/chat/conversations/{id}/archive: one flag, so
 * archiving and unarchiving are the same route and the same audit action.
 */
export interface ArchiveConversation {
	archived: boolean;
}
/**
 * AddMembers is the body of POST /api/chat/conversations/{id}/members. History says what the added
 * people may read: the conversation's default window, or nothing that happened before the add.
 */
export interface AddMembers {
	userIds: number /* int64 */[];
	history: string; // full | fromNow
}
/**
 * MembersAdded is the answer of an add: the rows that were written and the conversation with its
 * new member count.
 */
export interface MembersAdded {
	members: MemberPublic[];
	conversation: ConversationView;
}
/**
 * PromoteConversation is the body of POST /api/chat/conversations/{id}/promote: add people to a DM
 * (which promotes it to a group DM in place), turn a group DM into a named private conversation, or
 * both in one call.
 */
export interface PromoteConversation {
	addUserIds?: number /* int64 */[];
	toChannel?: PromoteToNamed;
}
/**
 * PromoteToNamed is the slug and name a group DM takes when it becomes a named conversation.
 */
export interface PromoteToNamed {
	slug: string;
	name: string;
}
/**
 * UpdateSelf is the body of PATCH /api/chat/conversations/{id}/self — the private half of the
 * membership row. Every field is optional; an absent one is left as it is.
 */
export interface UpdateSelf {
	notifyLevel?: string;
	mutedUntil?: number /* int64 */;
	starred?: boolean;
	hidden?: boolean;
	sortHint?: number /* int */;
}
/**
 * SelfResponse is the answer of the two routes that return only the caller's own row: the self
 * patch and the join.
 */
export interface SelfResponse {
	self: MemberSelf;
}
/**
 * AddOwners is the body of POST /api/chat/conversations/{id}/owners: the groups that from now on
 * find, read and may join the channel.
 */
export interface AddOwners {
	groupIds: number /* int64 */[];
}
/**
 * SetAgents is the body of PUT /api/chat/conversations/{id}/agents: whether AI chats may read the
 * conversation from now on.
 */
export interface SetAgents {
	available: boolean;
}
/**
 * AdminConversationRow is one row of the administrator's listing of the channels and private
 * channels they are not in: a name, the owner groups and how many people are in it — never who —,
 * and whether it is open to AI chats, by whom and since when (the administrators' page *Open to AI
 * chats* reads the flag; the listing itself shows no column of it).
 */
export interface AdminConversationRow {
	id: number /* int64 */;
	kind: string; // channel | private
	slug: string;
	name: string;
	ownerGroupIds: number /* int64 */[];
	memberCount: number /* int */;
	archived: boolean;
	agentsAvailable: boolean;
	agentsSetBy?: number /* int64 */;
	agentsSetAt?: number /* int64 */;
}
/**
 * AdminConversationList is the answer of GET /api/admin/chat/conversations.
 */
export interface AdminConversationList {
	conversations: AdminConversationRow[];
}
/**
 * ChannelSummary is one row of the browse list: a group channel the reader reaches or is in, with
 * whether they are in it.
 */
export interface ChannelSummary {
	conversation: ConversationView;
	joined: boolean;
}
/**
 * ChannelList is the answer of GET /api/chat/channels.
 */
export interface ChannelList {
	channels: ChannelSummary[];
}
/**
 * OpenDm is the body of POST /api/chat/dm: the people to talk to, without the caller.
 */
export interface OpenDm {
	userIds: number /* int64 */[];
}
/**
 * DmOpened is the answer of the DM route: the conversation that was found or created, and the
 * caller's own row in it.
 */
export interface DmOpened {
	conversation: ConversationView;
	self: MemberSelf;
}

//////////
// source: draft.go

/**
 * The three targets a draft can belong to: the conversation's composer, a thread's composer, or the
 * edit box of one message. The wire spells the third one "messageEdit"; schema.md §3.4 stores it as
 * "message_edit", and the drafts package is the one place the two spellings meet.
 */
export const DraftTargetConversation = "conversation";
/**
 * The three targets a draft can belong to: the conversation's composer, a thread's composer, or the
 * edit box of one message. The wire spells the third one "messageEdit"; schema.md §3.4 stores it as
 * "message_edit", and the drafts package is the one place the two spellings meet.
 */
export const DraftTargetThread = "thread";
/**
 * The three targets a draft can belong to: the conversation's composer, a thread's composer, or the
 * edit box of one message. The wire spells the third one "messageEdit"; schema.md §3.4 stores it as
 * "message_edit", and the drafts package is the one place the two spellings meet.
 */
export const DraftTargetMessageEdit = "messageEdit";
/**
 * DraftView is a whole draft, text included: the answer of chat.draft.get, one row of
 * chat.draft.list, and the detail of the conflict a stale put is refused with.
 */
export interface DraftView {
	targetKind: string;
	targetId: number /* int64 */;
	conv: number /* int64 */;
	markdown: string;
	attachments: core.AttachmentRef[];
	replyToId?: number /* int64 */;
	baseVersion?: number /* int */;
	rev: number /* int64 */;
	updatedAt: number /* int64 */;
	deviceId?: number /* int64 */;
}
/**
 * DraftDigest is a draft without its text: what rt.bootstrap carries per draft (protocol.md §2.7)
 * so the sidebar can draw its "there is a draft here" pill before any body is fetched.
 */
export interface DraftDigest {
	targetKind: string;
	targetId: number /* int64 */;
	conv: number /* int64 */;
	rev: number /* int64 */;
	updatedAt: number /* int64 */;
	bytes: number /* int */;
	attachmentCount: number /* int */;
}
/**
 * DraftTombstone is a deleted draft as the other devices learn of it: the rev the draft carried
 * when it went, which is what stops an offline device from putting the old text back.
 */
export interface DraftTombstone {
	targetKind: string;
	targetId: number /* int64 */;
	rev: number /* int64 */;
	deletedAt: number /* int64 */;
}
/**
 * DraftPut writes one draft. BaseRev is the rev the caller last saw, 0 for a target it has never
 * synced; an empty Markdown with no attachments is a delete, so the composer needs no second call
 * when the user clears the box.
 */
export interface DraftPut {
	targetKind: string;
	targetId: number /* int64 */;
	markdown: string;
	attachments: core.AttachmentRef[];
	replyToId?: number /* int64 */;
	baseVersion?: number /* int */;
	baseRev: number /* int64 */;
}
/**
 * DraftPutAck answers a put. Deleted says the draft is gone rather than stored — either because the
 * put emptied it, or because a tombstone at or above BaseRev already retired it.
 */
export interface DraftPutAck {
	rev: number /* int64 */;
	updatedAt: number /* int64 */;
	deleted: boolean;
}
/**
 * DraftDelete drops one draft; BaseRev is the rev the caller last saw.
 */
export interface DraftDelete {
	targetKind: string;
	targetId: number /* int64 */;
	baseRev: number /* int64 */;
}
/**
 * DraftDeleteAck carries the rev the tombstone now holds.
 */
export interface DraftDeleteAck {
	rev: number /* int64 */;
}
/**
 * DraftGet asks for one draft's text.
 */
export interface DraftGet {
	targetKind: string;
	targetId: number /* int64 */;
}
/**
 * DraftGetResult is the answer: the draft, or nothing plus the rev of the tombstone that retired
 * it, so a device that missed the event learns why the text is gone.
 */
export interface DraftGetResult {
	draft?: DraftView;
	tombstoneRev?: number /* int64 */;
}
/**
 * DraftList asks for every draft the caller owns; SinceRev limits the answer to what changed after
 * a rev the caller already has.
 */
export interface DraftList {
	sinceRev?: number /* int64 */;
}
/**
 * DraftListResult is the whole mirror of one account: the live drafts and the tombstones that
 * retired the others.
 */
export interface DraftListResult {
	drafts: DraftView[];
	tombstones: DraftTombstone[];
}
/**
 * DraftFlush is the body of POST /api/chat/drafts/flush, the pagehide path. sendBeacon cannot set a
 * header, so the CSRF token travels in the body and the origin check runs before it (security.md
 * §7, protocol.md §3.1).
 */
export interface DraftFlush {
	csrfToken: string;
	drafts: DraftPut[];
}
/**
 * DraftChanged tells the account's other devices that a draft moved on. It carries the digest and
 * no body: the replay log keeps events for 30 days and a draft's text is not something to keep
 * there (protocol.md §8 R7). A device whose composer is empty answers it with chat.draft.get.
 */
export interface DraftChanged {
	targetKind: string;
	targetId: number /* int64 */;
	conv: number /* int64 */;
	rev: number /* int64 */;
	updatedAt: number /* int64 */;
	deviceId?: number /* int64 */;
	bytes: number /* int */;
	attachmentCount: number /* int */;
}
/**
 * DraftDeleted retires a draft on every device of its owner — sent when the user empties the
 * composer, deletes the draft, or sends the message the draft was written for (protocol.md §8 R14).
 */
export interface DraftDeleted {
	targetKind: string;
	targetId: number /* int64 */;
	rev: number /* int64 */;
	deletedAt: number /* int64 */;
}

//////////
// source: events.go

/**
 * MessageEdited is chat.message.edited: the whole new state of the body, never a delta.
 */
export interface MessageEdited {
	id: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	version: number /* int */;
	markdown: string;
	render: core.Render;
	/**
	 * RenderOmitted says the tree above is empty because the real one would have carried this event
	 * over the change_log ceiling, not because the message has none — the same case, and the same
	 * answer, as MessageView.RenderOmitted: the client fetches the message and keeps its markdown
	 * on screen meanwhile.
	 */
	renderOmitted?: boolean;
	attachments: core.AttachmentWire[];
	mentions: MentionRef[];
	editedAt: number /* int64 */; // 0 while the edit collapsed into the grace window
	editedBy: number /* int64 */;
	collapsed: boolean;
}
/**
 * MessageDeleted is chat.message.deleted. The seq stays allocated (RT-SEQ): the client keeps the
 * row and draws the stub, "removed by an admin" when ByAdmin is set.
 */
export interface MessageDeleted {
	id: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	deletedAt: number /* int64 */;
	deletedBy: number /* int64 */;
	byAdmin: boolean;
	/**
	 * ThreadRootId names the root of a deleted reply, so a client whose thread window is cold can
	 * still take one off the root's reply count without loading the thread.
	 */
	threadRootId?: number /* int64 */;
}
/**
 * RenderUpdated is chat.render.updated: the tree changed, the markdown did not. It is sent only by
 * the render_ver backfill (spec 06 §6), and it is *ephemeral*: the tree is a cache, a client that
 * loads the page is handed the current one with it, and a durable row per re-rendered message would
 * put the whole archive through the change log and every catch-up for the next thirty days
 * (store-8). A reader who was not connected learns nothing of it, which is the design.
 */
export interface RenderUpdated {
	id: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	render: core.Render;
}

//////////
// source: files.go

/**
 * ConversationFile is one file of a conversation: the attachment as every other surface carries it,
 * with the message it hangs on. The embedded shape is flattened into the row's own JSON object, so a
 * client parses one attachment and not an attachment inside a wrapper (protocol.md §5.1).
 * MessageId and Seq are what make a row a link: the seq addresses the message inside the
 * conversation (/c/<slug>/p/<seq>), and the id is what a later per-file action would name. Both are
 * columns of the attachment row, written when the message bound it.
 */
export interface ConversationFile extends core.AttachmentWire {
	messageId: number /* int64 */;
	seq: number /* int64 */;
	uploaderId: number /* int64 */;
	/**
	 * CreatedAt is when the bytes were uploaded, not when the message was sent. The two are seconds
	 * apart for every file a composer ever sent, and this one is a column of the row the list reads.
	 */
	createdAt: number /* int64 */;
}
/**
 * FilePage is one page of the list. HasMore is the paging signal, and the cursor for the next page
 * is the id of the last row: a client pages with what it already holds rather than with a token it
 * has to keep.
 */
export interface FilePage {
	files: ConversationFile[];
	hasMore: boolean;
}

//////////
// source: message.go

/**
 * MessageView is one message as every member of its conversation sees it. A tombstone carries no
 * markdown, no render and no attachments — only the seq it keeps and who removed it (Q16).
 */
export interface MessageView {
	id: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	authorId?: number /* int64 */; // 0 on a system row
	authorKind: string; // user | system | bot
	onBehalfOfId?: number /* int64 */; // A38: the human a bot spoke for; never from the client
	/**
	 * Kind is "text" for every client send, or a system kind (spec 05 §2.6) minted by the service.
	 */
	kind: string;
	/**
	 * Meta is the payload of a system kind: ids and enums the client renders and localises.
	 */
	meta?: unknown;
	/**
	 * Markdown is the canonical source; "" on a tombstone and on a system row.
	 */
	markdown: string;
	render?: core.Render; // nil on a tombstone and a system row
	/**
	 * RenderOmitted marks the one case in which a *readable* message travels without its tree: the
	 * tree alone is larger than the 64 KiB a change_log row may hold, so the event carries the
	 * message and not the rendering of it (chat-1; protocol.md §2.5.3). It is set on events only —
	 * a route answers the whole message — and it is what tells the client to fetch the tree through
	 * the message route instead of showing the markdown for ever.
	 */
	renderOmitted?: boolean;
	mentions?: MentionRef[];
	attachments?: core.AttachmentWire[];
	reactions?: ReactionSummary[];
	threadRootId?: number /* int64 */;
	replyToId?: number /* int64 */;
	/**
	 * AlsoToChannel marks a reply its sender also sent to the conversation: it is in the timeline
	 * as well as in its thread, and the timeline draws the line that says which thread it answers
	 * (X-A1 A4). It is decided at send time and never changes.
	 */
	alsoToChannel?: boolean;
	replyCount?: number /* int */; // maintained on a thread root only
	lastReplyAt?: number /* int64 */;
	/**
	 * LastReplySeq is the seq of the newest live reply: the number the client compares against its
	 * own read cursor to decide whether the root's thread footer is bold.
	 */
	lastReplySeq?: number /* int64 */;
	/**
	 * ReplyUserIds are the first five people who replied, oldest reply first — the footer's avatars.
	 * It is filled only for a root a history page carries, never on an event.
	 */
	replyUserIds?: number /* int64 */[];
	clientMsgId?: string; // so the author's other tabs collapse the echo
	createdAt: number /* int64 */;
	editedAt?: number /* int64 */; // set only by a non-collapsed edit
	version: number /* int */; // the optimistic-concurrency token of an edit
	deletedAt?: number /* int64 */;
	deletedBy?: number /* int64 */;
	byAdmin?: boolean; // the remover was not the author
}
/**
 * Send is the payload of chat.message.send. Attachments is the complete ordered set the message
 * starts with (protocol.md §2.6); ThreadRootId and ReplyToId are both optional and independent.
 */
export interface Send {
	conv: number /* int64 */;
	clientMsgId: string; // UUID minted by the sender; the idempotency key
	markdown: string;
	attachments?: core.AttachmentRef[];
	threadRootId?: number /* int64 */;
	replyToId?: number /* int64 */;
	/**
	 * AlsoToChannel asks for a reply to stand in the conversation's timeline as well. It is read
	 * only beside a ThreadRootId: a message that is not a reply is in the timeline anyway.
	 */
	alsoToChannel?: boolean;
}
/**
 * SendAck answers a send. A retransmission with the same clientMsgId receives the original ack
 * byte for byte except Dedup, which is true (protocol.md §2.3).
 */
export interface SendAck {
	clientMsgId: string;
	id: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	createdAt: number /* int64 */;
	version: number /* int */;
	dedup: boolean;
}
/**
 * Edit is the payload of chat.message.edit. BaseVersion is the version the edit was written
 * against; a mismatch is refused with EditConflict as the error detail. Attachments is the complete
 * ordered set the message ends with — an id that is missing from it is unbound.
 */
export interface Edit {
	id: number /* int64 */;
	clientMsgId: string;
	markdown: string;
	attachments?: core.AttachmentRef[];
	baseVersion: number /* int */;
}
/**
 * EditAck answers an edit. EditedAt is null for a collapsed edit: the typo fix inside the grace
 * window that raised the version without showing an "(edited)" marker (Q16).
 */
export interface EditAck {
	id: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	version: number /* int */;
	editedAt?: number /* int64 */;
	collapsed: boolean;
	dedup: boolean;
}
/**
 * EditConflict is the detail of error.chat.editConflict: the state the message has now, so the
 * client can show both bodies instead of losing either (spec 08 §3.1).
 */
export interface EditConflict {
	currentVersion: number /* int */;
	currentMarkdown: string;
	currentAttachments: core.AttachmentWire[];
	editedAt?: number /* int64 */;
	editedBy?: number /* int64 */;
}
/**
 * Delete is the payload of chat.message.delete — the author's own delete; an admin deletes over
 * HTTP (protocol.md §3.2).
 */
export interface Delete {
	id: number /* int64 */;
	clientMsgId: string;
}
/**
 * DeleteAck answers a delete. ByAdmin is false on this path by construction.
 */
export interface DeleteAck {
	id: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	deletedAt: number /* int64 */;
	deletedBy: number /* int64 */;
	byAdmin: boolean;
	dedup: boolean;
}
/**
 * HistoryPage is one page of GET /api/chat/conversations/{id}/messages, and of a thread page. It
 * carries the reader's own membership row so a per-conversation resync has everything in one
 * answer (protocol.md §8 R12), and VisibleFromSeq so the timeline can draw the floor divider
 * instead of scrolling for ever. EventId is the change_log head the page was read at.
 */
export interface HistoryPage {
	messages: MessageView[];
	hasMoreBefore: boolean;
	hasMoreAfter: boolean;
	lastSeq: number /* int64 */;
	visibleFromSeq: number /* int64 */;
	self?: MemberSelf;
	eventId: number /* int64 */;
	/**
	 * Root is the thread's root, on every page of a thread and whatever the page is scrolled to, so
	 * the pane has one shape however deep the reader has paged. It is nil on a conversation page.
	 */
	root?: MessageView;
}
/**
 * MessageRead is the answer of the permalink route GET …/conversations/{id}/messages/{seq}.
 */
export interface MessageRead {
	message: MessageView;
	conversation: ConversationView;
}
/**
 * MessageResponse is the answer of every route that returns one message alone: the admin delete
 * and the restore.
 */
export interface MessageResponse {
	message: MessageView;
}
/**
 * VersionView is one superseded body of a message, as the edit-history popover lists it. Reason
 * says why the body stopped being current; Collapsed marks a typo fix the popover hides.
 */
export interface VersionView {
	version: number /* int */;
	markdown: string;
	attachments: core.AttachmentWire[];
	editedAt: number /* int64 */;
	editedBy?: number /* int64 */;
	reason: string; // edit | delete | restore | anonymise | purge
	collapsed: boolean;
}
/**
 * MessageVersions is the answer of GET /api/chat/messages/{id}/versions: the superseded bodies,
 * newest first, and the current one (history = versions ∪ {current}, spec 05 §2.4).
 */
export interface MessageVersions {
	versions: VersionView[];
	current: VersionView;
}

//////////
// source: pin.go

/**
 * PinView is one pin as the conversation's members see it.
 */
export interface PinView {
	messageId: number /* int64 */;
	conv: number /* int64 */;
	/**
	 * Seq is the pinned message's place in the stream: the permalink a pinned card opens, and the
	 * number the delivery-time visibility floor is applied to.
	 */
	seq: number /* int64 */;
	byUserId: number /* int64 */;
	at: number /* int64 */;
}
/**
 * PinAck is the body of PUT /api/chat/messages/{id}/pin: the pin as it stands after the request,
 * which on a repeat is the original — the unique row is the idempotency.
 */
export interface PinAck {
	pin: PinView;
}
/**
 * PinnedMessage is one entry of the pinned list: the pin and the message it fastens, so a card is
 * drawn from one row.
 */
export interface PinnedMessage {
	pin: PinView;
	message: MessageView;
}
/**
 * PinnedPage is the body of GET /api/chat/conversations/{id}/pins, newest pin first. A deleted
 * message leaves the list rather than standing in it as a tombstone (X-A4 N1).
 */
export interface PinnedPage {
	pinned: PinnedMessage[];
	hasMore: boolean;
}
/**
 * PinChanged is chat.pin.changed, to the whole conversation: a message was pinned or unpinned. It
 * names the message and the seq inside the payload — the event allocates no seq of its own
 * (protocol.md §2.4) — and carries no projection: every reader draws the same fact.
 */
export interface PinChanged {
	conv: number /* int64 */;
	messageId: number /* int64 */;
	seq: number /* int64 */;
	pinned: boolean;
	byUserId: number /* int64 */;
	at: number /* int64 */;
}

//////////
// source: presence.go

/**
 * PresenceUser is one person in the picture.
 */
export interface PresenceUser {
	userId: number /* int64 */;
	state: string; // "online" | "away" | "offline"
}
/**
 * Presence is chat.presence. Full says the list is the whole picture and not a change to it, which is
 * what lets a client replace rather than merge; it is true in every frame this server sends.
 */
export interface Presence {
	users: PresenceUser[];
	full: boolean;
}

//////////
// source: reaction.go

/**
 * ReactionSummary is one chip under a message: the whole state of one emoji on it, never a delta,
 * so a client that missed a frame is repaired by the next one. UserIds is capped at 20 and ordered
 * by the moment each reaction was added (A29).
 * Me is a per-viewer fact and is therefore set only where a summary has one viewer — the answer to
 * that viewer's own request. In chat.reaction.changed, which one event carries to everybody in the
 * conversation, it is always false and each client derives its own from UserIds.
 */
export interface ReactionSummary {
	emoji: string;
	count: number /* int */;
	me: boolean;
	userIds: number /* int64 */[];
}
/**
 * ReactionSet is the request behind both chat.reaction.add and chat.reaction.remove. It needs no
 * idempotency key: the unique row is the idempotency, and a repeat answers the current summary.
 */
export interface ReactionSet {
	messageId: number /* int64 */;
	emoji: string;
}
/**
 * ReactionAck is the reply to either request: the summary as it now stands, with the message's
 * conversation and seq so a client that has not loaded the message can still place the chip.
 */
export interface ReactionAck {
	messageId: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	summary: ReactionSummary;
}
/**
 * ReactionChanged is the conversation event. The seq is the message's, carried inside the data:
 * the envelope's seq belongs to seq-allocating events alone (protocol.md §2.4).
 */
export interface ReactionChanged {
	messageId: number /* int64 */;
	conv: number /* int64 */;
	seq: number /* int64 */;
	emoji: string;
	summary: ReactionSummary;
	actorUserId: number /* int64 */;
}
/**
 * ReactionUsers is the body of GET /api/chat/messages/{id}/reactions/{emoji}/users: who reacted,
 * projected for the viewer. It is the full list, which the chip's tooltip needs and the capped
 * UserIds of a summary cannot give.
 */
export interface ReactionUsers {
	users: core.PublicUser[];
}
/**
 * EmojiUsed is the fire-and-forget request the picker sends after an insertion, so "recently used"
 * follows a person from device to device. At most 20 sequences per frame.
 */
export interface EmojiUsed {
	emoji: string[];
}
/**
 * EmojiRecents is both the body of GET /api/chat/emoji/recents and the event the acting user's
 * other connections receive. SkinTone is 0 (neutral) to 5 and lives in the user setting
 * chat.emoji.skin_tone, not in the usage table: it is a preference, not a history.
 */
export interface EmojiRecents {
	emoji: string[];
	skinTone: number /* int */;
}
/**
 * Typing is the typist's frame: where they are writing, and nothing else. What the server broadcasts
 * is TypingEvent, and the identity in it comes from the connection, never from this payload.
 * ThreadRootId is the box the keystrokes are in: a thread's reply box rather than the conversation's
 * own (X-A1 A5). It is a field of the existing payload and not a second action — the authorisation
 * is the conversation's either way, because a thread is inside it — and an absent one is the
 * conversation, which is what every client before threads sent.
 */
export interface Typing {
	conv: number /* int64 */;
	threadRootId?: number /* int64 */;
}
/**
 * TypingEvent is the ephemeral event the other members receive. It carries no event id and is never
 * replayed; the client forgets it after five seconds. ThreadRootId is echoed from the frame, so the
 * line appears under the box the typist is actually writing in and nowhere else.
 */
export interface TypingEvent {
	conv: number /* int64 */;
	userId: number /* int64 */;
	threadRootId?: number /* int64 */;
	at: number /* int64 */;
}

//////////
// source: types.go
/*
Package wire is the chat module's half of the socket and HTTP vocabulary: every type string the
module sends or answers, and one struct per request, reply and event. It is a tygo input
(tygo.yaml → web/src/modules/chat/wire/chat.gen.ts), so it holds no `any` and no behaviour —
a payload whose shape belongs to another owner travels as json.RawMessage.

The subpackage rule of architecture.md D3 applies here too: wire imports core and nothing of the
module root.
*/

/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatMessageSend = "chat.message.send";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatMessageEdit = "chat.message.edit";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatMessageDelete = "chat.message.delete";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatReactionAdd = "chat.reaction.add";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatReactionRemove = "chat.reaction.remove";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatEmojiUsed = "chat.emoji.used";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatReadMark = "chat.read.mark";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatReadMarkUnreadFrom = "chat.read.markUnreadFrom";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatReadMarkAll = "chat.read.markAll";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatReadRestore = "chat.read.restore";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatDraftPut = "chat.draft.put";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatDraftDelete = "chat.draft.delete";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatDraftGet = "chat.draft.get";
/**
 * Client → server requests (protocol.md §2.5.3 – §2.5.6).
 */
export const TypeChatDraftList = "chat.draft.list";
/**
 * TypeChatTyping travels in both directions: the client posts one under realtime.typing.send and
 * the hub broadcasts the same type back as an ephemeral event (protocol.md §2.5.4). It is therefore
 * a member of both lists below.
 */
export const TypeChatTyping = "chat.typing";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatMessageNew = "chat.message.new";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatMessageEdited = "chat.message.edited";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatMessageDeleted = "chat.message.deleted";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatRenderUpdated = "chat.render.updated";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatReactionChanged = "chat.reaction.changed";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatEmojiRecents = "chat.emoji.recents";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatPresence = "chat.presence";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatPinChanged = "chat.pin.changed";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatReadState = "chat.read.state";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatDraftChanged = "chat.draft.changed";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatDraftDeleted = "chat.draft.deleted";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatConversationCreated = "chat.conversation.created";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatConversationUpdated = "chat.conversation.updated";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatMemberChanged = "chat.member.changed";
/**
 * Server → client events (protocol.md §2.5.3 – §2.5.7).
 */
export const TypeChatMembershipSelf = "chat.membership.self";
/**
 * The conversation kinds as the client reads them. The database writes group_dm with an underscore
 * (schema.md §3.1); the wire is camelCase like every other enum of protocol.md, and model.go holds
 * the one conversion between the two.
 */
export const KindChannel = "channel";
/**
 * The conversation kinds as the client reads them. The database writes group_dm with an underscore
 * (schema.md §3.1); the wire is camelCase like every other enum of protocol.md, and model.go holds
 * the one conversion between the two.
 */
export const KindPrivate = "private";
/**
 * The conversation kinds as the client reads them. The database writes group_dm with an underscore
 * (schema.md §3.1); the wire is camelCase like every other enum of protocol.md, and model.go holds
 * the one conversion between the two.
 */
export const KindDm = "dm";
/**
 * The conversation kinds as the client reads them. The database writes group_dm with an underscore
 * (schema.md §3.1); the wire is camelCase like every other enum of protocol.md, and model.go holds
 * the one conversion between the two.
 */
export const KindGroupDm = "groupDm";
/**
 * The membership states and the notification levels a member row carries, as the client reads them.
 */
export const MemberStateActive = "active";
/**
 * The membership states and the notification levels a member row carries, as the client reads them.
 */
export const MemberStateLeft = "left";
/**
 * The membership states and the notification levels a member row carries, as the client reads them.
 */
export const MemberStateRemoved = "removed";
/**
 * The membership states and the notification levels a member row carries, as the client reads them.
 */
export const NotifyAll = "all";
/**
 * The membership states and the notification levels a member row carries, as the client reads them.
 */
export const NotifyMention = "mention";
/**
 * The membership states and the notification levels a member row carries, as the client reads them.
 */
export const NotifyNone = "none";

//////////
// source: unread.go

/**
 * CountCap is the ceiling of Unread and Mentions. Above it the number stops being information and
 * starts being noise, and the client renders "999+" (A17).
 */
export const CountCap = 999;
/**
 * The two scopes of chat.read.markAll: every conversation the caller can see, or one of them.
 */
export const MarkAllScopeAll = "all";
/**
 * The two scopes of chat.read.markAll: every conversation the caller can see, or one of them.
 */
export const MarkAllScopeConv = "conv";
/**
 * CursorState is one conversation's read state as its owner sees it. Unread and Mentions are counts
 * of what lies after LastReadSeq, capped at CountCap; Version is the optimistic-concurrency token
 * that every accepted write increments.
 */
export interface CursorState {
	conv: number /* int64 */;
	lastReadSeq: number /* int64 */;
	version: number /* int64 */;
	manualUnread: boolean;
	unread: number /* int */;
	mentions: number /* int */;
}
/**
 * CursorRef is a cursor without its counts: what "mark all read" hands back for the undo, and what
 * the undo hands in. The counts are derived, so they are never part of an instruction. Seq is where
 * the cursor is to go, Version the value it must still carry for that to be allowed.
 */
export interface CursorRef {
	conv: number /* int64 */;
	seq: number /* int64 */;
	version: number /* int64 */;
}
/**
 * ReadMark advances a cursor. Auto marks the viewport rule rather than a deliberate click, which is
 * what lets "mark unread from here" survive the reader's own scrolling.
 */
export interface ReadMark {
	conv: number /* int64 */;
	seq: number /* int64 */;
	version: number /* int64 */;
	auto: boolean;
}
/**
 * ReadMarkUnreadFrom moves the cursor to Seq-1 and holds it there until the next deliberate read.
 */
export interface ReadMarkUnreadFrom {
	conv: number /* int64 */;
	seq: number /* int64 */;
	version: number /* int64 */;
}
/**
 * ReadMarkAll clears the badges of one conversation or of all of them.
 */
export interface ReadMarkAll {
	scope: string;
	conv?: number /* int64 */;
}
/**
 * ReadMarkAllAck is the pre-state of everything the call moved, in the order it was moved: the undo
 * hands exactly this array back as ReadRestore.Cursors.
 */
export interface ReadMarkAllAck {
	restore: CursorRef[];
}
/**
 * ReadRestore is the undo of a mark-all. It is the only path that may move a cursor backwards in
 * bulk, and only while nothing else has moved it since.
 */
export interface ReadRestore {
	cursors: CursorRef[];
}
/**
 * ReadRestoreAck says what the undo did: the new state of every restored conversation, and the ids
 * of the ones whose cursor had moved on and were therefore left alone.
 */
export interface ReadRestoreAck {
	applied: CursorState[];
	skipped: number /* int64 */[];
}
/**
 * The three kinds of MentionRef.Kind. An empty Kind is an unresolved token: the handle named nothing
 * this instance knows, so the body keeps it as literal text. "channel" is also one of the two mention
 * tokens this module registers with core (mention_tokens.go).
 */
export const MentionKindUser = "user";
/**
 * The three kinds of MentionRef.Kind. An empty Kind is an unresolved token: the handle named nothing
 * this instance knows, so the body keeps it as literal text. "channel" is also one of the two mention
 * tokens this module registers with core (mention_tokens.go).
 */
export const MentionKindGroup = "group";
/**
 * The three kinds of MentionRef.Kind. An empty Kind is an unresolved token: the handle named nothing
 * this instance knows, so the body keeps it as literal text. "channel" is also one of the two mention
 * tokens this module registers with core (mention_tokens.go).
 */
export const MentionKindChannel = "channel";
/**
 * MentionKindConversation is a `#slug` reference to a conversation, the other mention token this
 * module registers. It is the one mention kind that is *never* resolved on the server: the handle is
 * the conversation's short name and Id stays 0, because whether that name points at something the
 * reader may see — and therefore whether the token is a chip at all — is a property of the reader and
 * not of the message, and a cached render tree is one row shared by everybody. The client answers it
 * from its own model (X-A4 N6). It is never a MentionRef.Kind: a place names no one to notify.
 */
export const MentionKindConversation = "conversation";
/**
 * MentionRef is one resolved mention token of a message (protocol.md §2.6). A group mention names
 * the group once — the per-member rows it expands into are server-side only (schema.md §3.2) — and
 * @channel names neither a user nor a group.
 * It carries no handle: the resolver answers one ref per token in the order the body carries them,
 * so the caller that has the tokens already knows which handle a ref belongs to, and a client
 * resolves an id to a name through the accounts and groups it was given at bootstrap.
 */
export interface MentionRef {
	kind: string;
	userId?: number /* int64 */;
	groupId?: number /* int64 */;
}
